// Fullscreen viewer. Three-pane track (prev/current/next) that follows the finger,
// pinch/double-tap/wheel zoom on images, keyboard navigation, swipe-down to close,
// original-quality download. Videos play with native controls; gestures ignore
// pointer events that start on the <video> so its controls stay usable.
import React, { useEffect, useRef, useState } from 'react';
import { displayUrl, downloadUrl, posterUrl } from '../api.js';
import { GALLERY } from '../text.js';
import { ChevronIcon, CloseIcon, DownloadIcon, TrashIcon } from './icons.jsx';

const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const NAV_THRESHOLD = 60; // px of horizontal drag that commits a prev/next
const CLOSE_THRESHOLD = 90; // px of vertical drag that closes
const SNAP_MS = 260;

export default function Lightbox({ items, onClose, onDelete, openIdx }) {
	const [idx, setIdx] = useState(openIdx ?? 0);
	const [syncedOpen, setSyncedOpen] = useState(openIdx);
	const [drag, setDrag] = useState(0);
	const [snapping, setSnapping] = useState(false);
	const [zoom, setZoom] = useState({ s: 1, tx: 0, ty: 0 });
	const [gesturing, setGesturing] = useState(false);

	const stageRef = useRef(null);
	const pointers = useRef(new Map());
	const gesture = useRef(null); // pinch/pan/swipe bookkeeping between events
	const lastTap = useRef({ t: 0, x: 0, y: 0 });
	const snapTimer = useRef(null);

	// Sync to a fresh openIdx during render (never in an effect) so the first frame
	// after opening can't show a stale index — see the weltreise crash-fix note.
	if (openIdx !== syncedOpen) {
		setSyncedOpen(openIdx);
		if (openIdx != null) {
			setIdx(openIdx);
			setDrag(0);
			setZoom({ s: 1, tx: 0, ty: 0 });
		}
	}

	const count = items.length;
	const safeIdx = Math.min(Math.max(idx, 0), count - 1);
	const zoomed = zoom.s > 1.02;

	const resetZoom = () => setZoom({ s: 1, tx: 0, ty: 0 });

	const goTo = (next, animate) => {
		if (next < 0 || next >= count || next === safeIdx) return;
		if (animate) {
			// Slide the track one pane over, then commit the index and recenter.
			setSnapping(true);
			setDrag(next > safeIdx ? -window.innerWidth : window.innerWidth);
			snapTimer.current = setTimeout(() => {
				setSnapping(false);
				setDrag(0);
				setIdx(next);
				resetZoom();
			}, SNAP_MS);
		} else {
			setIdx(next);
			setDrag(0);
			resetZoom();
		}
	};

	useEffect(() => () => clearTimeout(snapTimer.current), []);

	useEffect(() => {
		if (openIdx == null) return;
		const onKey = (e) => {
			if (e.key === 'Escape') onClose();
			else if (e.key === 'ArrowRight') goTo(Math.min(count - 1, safeIdx + 1), false);
			else if (e.key === 'ArrowLeft') goTo(Math.max(0, safeIdx - 1), false);
		};
		window.addEventListener('keydown', onKey);
		document.body.style.overflow = 'hidden';
		return () => {
			window.removeEventListener('keydown', onKey);
			document.body.style.overflow = '';
		};
	});

	if (openIdx == null || !count) return null;
	const item = items[safeIdx];
	if (!item) return null;

	// ---- zoom helpers -------------------------------------------------------
	// Content transform is translate(tx,ty) scale(s) around the stage centre:
	// screen = centre + t + s·p. Anchoring a point (pinch midpoint, tap, cursor)
	// solves t1 = m1 − c − (s1/s0)·(m0 − c − t0).
	const stageCentre = () => {
		const r = stageRef.current.getBoundingClientRect();
		return { x: r.left + r.width / 2, y: r.top + r.height / 2, w: r.width, h: r.height };
	};

	const clampPan = (z) => {
		if (!stageRef.current || !item.w || !item.h) return z;
		const c = stageCentre();
		// Rendered (contain-fitted) size of the image inside the stage.
		const fit = Math.min(c.w / item.w, c.h / item.h);
		const rw = item.w * fit * z.s;
		const rh = item.h * fit * z.s;
		const maxTx = Math.max(0, (rw - c.w) / 2);
		const maxTy = Math.max(0, (rh - c.h) / 2);
		return { s: z.s, tx: Math.min(maxTx, Math.max(-maxTx, z.tx)), ty: Math.min(maxTy, Math.max(-maxTy, z.ty)) };
	};

	const zoomAround = (mx, my, s1) => {
		const c = stageCentre();
		setZoom((z) => {
			const s = Math.min(MAX_SCALE, Math.max(1, s1));
			if (s === 1) return { s: 1, tx: 0, ty: 0 };
			const tx = mx - c.x - (s / z.s) * (mx - c.x - z.tx);
			const ty = my - c.y - (s / z.s) * (my - c.y - z.ty);
			return clampPan({ s, tx, ty });
		});
	};

	// ---- pointer gestures ---------------------------------------------------
	const onPointerDown = (e) => {
		if (snapping || e.target.closest('video, a, button')) return;
		e.currentTarget.setPointerCapture?.(e.pointerId);
		pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		setGesturing(true);

		if (pointers.current.size === 2) {
			const [a, b] = [...pointers.current.values()];
			gesture.current = {
				kind: 'pinch',
				startDist: Math.hypot(a.x - b.x, a.y - b.y),
				startScale: zoom.s,
				startZoom: { ...zoom },
				startMid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
			};
		} else if (pointers.current.size === 1) {
			gesture.current = {
				kind: zoomed ? 'pan' : 'swipe',
				startX: e.clientX,
				startY: e.clientY,
				startZoom: { ...zoom },
				moved: false,
			};
		}
	};

	const onPointerMove = (e) => {
		const p = pointers.current.get(e.pointerId);
		if (!p) return;
		pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		const g = gesture.current;
		if (!g) return;

		if (g.kind === 'pinch' && pointers.current.size === 2 && item.type === 'image') {
			const [a, b] = [...pointers.current.values()];
			const dist = Math.hypot(a.x - b.x, a.y - b.y);
			const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
			const c = stageCentre();
			const s = Math.min(MAX_SCALE, Math.max(1, g.startScale * (dist / g.startDist)));
			const tx = mid.x - c.x - (s / g.startZoom.s) * (g.startMid.x - c.x - g.startZoom.tx);
			const ty = mid.y - c.y - (s / g.startZoom.s) * (g.startMid.y - c.y - g.startZoom.ty);
			setZoom({ s, tx, ty });
		} else if (g.kind === 'pan') {
			setZoom(clampPan({ s: g.startZoom.s, tx: g.startZoom.tx + (e.clientX - g.startX), ty: g.startZoom.ty + (e.clientY - g.startY) }));
		} else if (g.kind === 'swipe') {
			const dx = e.clientX - g.startX;
			const dy = e.clientY - g.startY;
			if (Math.abs(dx) > 4 || Math.abs(dy) > 4) g.moved = true;
			// Rubber-band at the ends so the first/last item signals "no further".
			const atEdge = (dx > 0 && safeIdx === 0) || (dx < 0 && safeIdx === count - 1);
			setDrag(atEdge ? dx * 0.3 : dx);
		}
	};

	const onPointerUp = (e) => {
		const g = gesture.current;
		pointers.current.delete(e.pointerId);

		if (g?.kind === 'pinch' && pointers.current.size < 2) {
			gesture.current = null;
			setZoom((z) => (z.s < 1.05 ? { s: 1, tx: 0, ty: 0 } : clampPan(z)));
		} else if (g?.kind === 'pan' && pointers.current.size === 0) {
			gesture.current = null;
		} else if (g?.kind === 'swipe' && pointers.current.size === 0) {
			gesture.current = null;
			const dx = e.clientX - g.startX;
			const dy = e.clientY - g.startY;

			// Double-tap toggles zoom (touch); mouse users get dblclick + wheel.
			if (!g.moved && e.pointerType === 'touch' && item.type === 'image') {
				const now = Date.now();
				const lt = lastTap.current;
				lastTap.current = { t: now, x: e.clientX, y: e.clientY };
				if (now - lt.t < 320 && Math.hypot(e.clientX - lt.x, e.clientY - lt.y) < 40) {
					lastTap.current = { t: 0, x: 0, y: 0 };
					zoomAround(e.clientX, e.clientY, zoomed ? 1 : DOUBLE_TAP_SCALE);
					setGesturing(false);
					return;
				}
			}

			if (Math.abs(dx) > NAV_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
				const next = dx < 0 ? safeIdx + 1 : safeIdx - 1;
				if (next >= 0 && next < count) {
					goTo(next, true);
					setGesturing(false);
					return;
				}
			}
			if (dy > CLOSE_THRESHOLD && Math.abs(dy) > Math.abs(dx) * 1.5) {
				onClose();
				return;
			}
			setDrag(0);
		}
		if (pointers.current.size === 0) setGesturing(false);
	};

	const onWheel = (e) => {
		if (item.type !== 'image') return;
		e.preventDefault();
		zoomAround(e.clientX, e.clientY, zoom.s * Math.exp(-e.deltaY * 0.0022));
	};

	const onDoubleClick = (e) => {
		if (item.type !== 'image' || e.target.closest('video, a, button')) return;
		zoomAround(e.clientX, e.clientY, zoomed ? 1 : DOUBLE_TAP_SCALE);
	};

	// ---- render -------------------------------------------------------------
	const paneW = typeof window !== 'undefined' ? window.innerWidth : 0;
	const trackStyle = {
		transform: `translateX(${-paneW + drag}px)`,
		transition: snapping ? `transform ${SNAP_MS}ms var(--ease-soft)` : drag === 0 && !gesturing ? `transform ${SNAP_MS}ms var(--ease-soft)` : 'none',
	};

	const renderPane = (i, pos) => {
		if (i < 0 || i >= count) return <div className='lb__pane' key={`empty-${pos}`} />;
		const it = items[i];
		const current = i === safeIdx;
		if (it.type === 'video') {
			return (
				<div className='lb__pane' key={it.id}>
					{current ? (
						// key remounts on navigation so the previous clip stops.
						<video className='lb__video' key={it.id} src={displayUrl(it)} poster={posterUrl(it)} controls autoPlay playsInline />
					) : (
						<img className='lb__img' src={posterUrl(it)} alt='' draggable={false} />
					)}
				</div>
			);
		}
		const zoomStyle =
			current && zoom.s !== 1
				? { transform: `translate(${zoom.tx}px, ${zoom.ty}px) scale(${zoom.s})`, transition: gesturing ? 'none' : 'transform 200ms var(--ease-soft)' }
				: { transition: gesturing ? 'none' : 'transform 200ms var(--ease-soft)' };
		return (
			<div className='lb__pane' key={it.id}>
				<img className='lb__img' src={displayUrl(it)} alt='' draggable={false} style={zoomStyle} />
			</div>
		);
	};

	return (
		<div className='lb' role='dialog' aria-modal='true' aria-label='Medienansicht'>
			<div className='lb__topbar'>
				<div className='lb__num'>
					{safeIdx + 1} <span>/</span> {count}
				</div>
				<div className='lb__actions'>
					{/* Guests can undo their own uploads — `mine` comes from the listing. */}
					{onDelete && item.mine && (
						<button type='button' className='lb__action lb__action--danger' onClick={() => onDelete(item)} aria-label={GALLERY.deleteOwn} title={GALLERY.deleteOwn}>
							<TrashIcon size={20} />
						</button>
					)}
					<a className='lb__action' href={downloadUrl(item)} aria-label='In Originalqualität herunterladen' title='Herunterladen'>
						<DownloadIcon size={20} />
					</a>
					<button type='button' className='lb__action' onClick={onClose} aria-label='Schließen'>
						<CloseIcon size={20} />
					</button>
				</div>
			</div>

			<div
				ref={stageRef}
				className='lb__stage'
				style={{ touchAction: 'none' }}
				onPointerDown={onPointerDown}
				onPointerMove={onPointerMove}
				onPointerUp={onPointerUp}
				onPointerCancel={onPointerUp}
				onWheel={onWheel}
				onDoubleClick={onDoubleClick}>
				<div className='lb__track' style={trackStyle}>
					{renderPane(safeIdx - 1, 'prev')}
					{renderPane(safeIdx, 'cur')}
					{renderPane(safeIdx + 1, 'next')}
				</div>
			</div>

			<div className='lb__nav'>
				<button type='button' onClick={() => goTo(safeIdx - 1, false)} disabled={safeIdx === 0} aria-label='Vorheriges'>
					<ChevronIcon dir='left' />
				</button>
				<button type='button' onClick={() => goTo(safeIdx + 1, false)} disabled={safeIdx === count - 1} aria-label='Nächstes'>
					<ChevronIcon />
				</button>
			</div>
		</div>
	);
}
